import { useRef } from "react";
import {
  BadgeCheck,
  Building2,
  ImagePlus,
  Link2,
  Mail,
  Shield,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import { FormField, FormInput } from "../form/FormField";
import { SettingsSectionHeader } from "./SettingsSectionHeader";
import {
  mapSettingsRpcError,
  type PartnerProfile,
} from "../../lib/partnerSettings";
import { useUpdatePartnerLogo } from "../../hooks/usePartnerSettings";
import { useI18n } from "../../i18n";

interface ProfileSettingsTabProps {
  profile: PartnerProfile;
}

export function ProfileSettingsTab({ profile }: ProfileSettingsTabProps) {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUpdatePartnerLogo();

  const onPickLogo = async (file: File | null) => {
    if (!file) return;
    try {
      await upload.mutateAsync(file);
      toast.success(t("settings.profile.toastSaved"));
    } catch (err) {
      toast.error(t(mapSettingsRpcError(err)));
    }
  };

  return (
    <section
      className="menu-panel"
      role="tabpanel"
      aria-label={t("settings.profile.title")}
    >
      <SettingsSectionHeader
        title={t("settings.profile.title")}
        hint={t("settings.profile.hint")}
        icon={Building2}
      />
      <div className="menu-panel__body space-y-5">
        <div className="settings-block">
          <div className="settings-block__head">
            <span className="settings-block__icon" aria-hidden>
              <ImagePlus className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div>
              <p className="settings-block__title">{t("settings.profile.logo")}</p>
              <p className="settings-block__sub">{t("settings.profile.logoHint")}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-muted)]">
              {profile.logo_url ? (
                <img
                  src={profile.logo_url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <Building2 className="h-7 w-7 text-[var(--text-muted)]" />
              )}
            </div>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => void onPickLogo(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                className="btn-primary min-h-[44px] px-4"
                disabled={upload.isPending}
                onClick={() => fileRef.current?.click()}
              >
                {upload.isPending
                  ? t("settings.profile.uploading")
                  : t("settings.profile.changeLogo")}
              </button>
            </div>
          </div>
        </div>

        <div className="settings-block">
          <div className="settings-block__head">
            <span className="settings-block__icon" aria-hidden>
              <Building2 className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div>
              <p className="settings-block__title">
                {t("settings.profile.businessInfo")}
              </p>
              <p className="settings-block__sub">
                {t("settings.profile.businessHint")}
              </p>
            </div>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <FormField
              label={t("settings.profile.name")}
              htmlFor="partner-name"
              icon={<Building2 className="h-4 w-4" />}
            >
              <FormInput id="partner-name" hasIcon value={profile.name} readOnly />
            </FormField>
            <FormField
              label={t("settings.profile.slug")}
              htmlFor="partner-slug"
              icon={<Link2 className="h-4 w-4" />}
            >
              <FormInput id="partner-slug" hasIcon value={profile.slug} readOnly />
            </FormField>
            <FormField
              label={t("settings.profile.status")}
              htmlFor="partner-status"
              icon={<BadgeCheck className="h-4 w-4" />}
            >
              <FormInput
                id="partner-status"
                hasIcon
                value={profile.status}
                readOnly
              />
            </FormField>
            <FormField
              label={t("settings.profile.contactEmail")}
              htmlFor="partner-email"
              icon={<Mail className="h-4 w-4" />}
            >
              <FormInput
                id="partner-email"
                hasIcon
                value={profile.contact_email ?? ""}
                readOnly
                placeholder={t("common.dash")}
              />
            </FormField>
            <FormField
              label={t("settings.profile.displayName")}
              htmlFor="partner-display"
              icon={<User className="h-4 w-4" />}
            >
              <FormInput
                id="partner-display"
                hasIcon
                value={profile.display_name ?? ""}
                readOnly
                placeholder={t("common.dash")}
              />
            </FormField>
            <FormField
              label={t("settings.profile.role")}
              htmlFor="partner-role"
              icon={<Shield className="h-4 w-4" />}
            >
              <FormInput
                id="partner-role"
                hasIcon
                value={profile.role ?? ""}
                readOnly
              />
            </FormField>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProfileSettingsSkeleton() {
  return (
    <section className="menu-panel">
      <div className="menu-panel__header">
        <div className="h-6 w-40 skeleton rounded-lg" />
      </div>
      <div className="menu-panel__body space-y-3">
        <div className="h-24 skeleton rounded-xl" />
        <div className="h-40 skeleton rounded-xl" />
      </div>
    </section>
  );
}
